import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  X,
  Calendar,
  Clock,
  Target,
  Sparkles,
  ShieldCheck,
  Heart,
  MessageCircle,
  Search,
  ArrowRight,
} from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

// ------------------- CATALOGO -------------------
const ITEMS = [
  {
    id: "torre-montessori",
    nome: "Torre Montessori",
    categoria: "Autonomia & Cucina",
    fascia: "12-48 mesi",
    fasciaKey: "1-4",
    breve: "L'invito quotidiano a partecipare alla vita di casa, in totale sicurezza.",
    eta_consigliata: "Dai 12 ai 48 mesi",
    periodo_utilizzo: "Circa 2-3 anni",
    a_cosa_serve:
      "Permette al bambino di raggiungere in autonomia il piano della cucina, del bagno o dei tavoli alti, favorendo la partecipazione attiva alle attività quotidiane della famiglia.",
    competenze: [
      "Autonomia e iniziativa",
      "Motricità grossolana",
      "Autostima e senso di competenza",
      "Cooperazione domestica",
    ],
    sicurezza:
      "Base larga e stabile, spondine di protezione su tutti i lati, altezza regolabile secondo la statura del bambino. Sempre sotto sorveglianza durante l'uso.",
    consigli:
      "È lo strumento che apre la cucina al bambino. Ricorda: non è un mobile decorativo, è un invito quotidiano a 'fare con te'. Usala per impastare, apparecchiare, lavarsi le mani — piccoli gesti che costruiscono grande fiducia.",
    durata_noleggio: "6-12 mesi",
  },
  {
    id: "sbarra-lettino",
    nome: "Sbarra per lettino",
    categoria: "Sonno & Sicurezza",
    fascia: "18 mesi - 4 anni",
    fasciaKey: "1-4",
    breve: "Un abbraccio silenzioso che accompagna il passaggio al letto grande.",
    eta_consigliata: "Dai 18 mesi ai 4 anni",
    periodo_utilizzo: "12-24 mesi",
    a_cosa_serve:
      "Protegge il bambino dalle cadute durante il delicato passaggio dal lettino con sponde al letto grande o al lettone dei genitori, garantendo un sonno sereno e sicuro.",
    competenze: [
      "Sicurezza affettiva notturna",
      "Autonomia nel sonno",
      "Transizione consapevole",
    ],
    sicurezza:
      "Fissaggio saldo al materasso, altezza minima di 20 cm sopra il materasso, verificare la stabilità prima di ogni notte. Non usare con materassi ad acqua.",
    consigli:
      "Il passaggio al letto grande è un momento delicato. La sbarra non è 'una barriera': è un abbraccio che rassicura. Aspetta che il bambino sia pronto e accompagna il cambiamento con un piccolo rituale sereno.",
    durata_noleggio: "3-6 mesi",
  },
  {
    id: "seggiolone-stokke",
    nome: "Seggiolone Evolutivo Stokke",
    categoria: "Pappa & Convivialità",
    fascia: "6 mesi in poi",
    fasciaKey: "6m-3+",
    breve: "Un seggiolone che cresce col bambino. Un rito di famiglia che si nutre di sguardi.",
    eta_consigliata: "Dai 6 mesi in poi",
    periodo_utilizzo: "Evolutivo: cresce con il bambino",
    a_cosa_serve:
      "Un seggiolone che accompagna il bambino dai primi assaggi fino all'adolescenza. Regolabile in altezza e profondità, permette di stare a tavola con la famiglia mantenendo la postura corretta.",
    competenze: [
      "Postura corretta a tavola",
      "Autonomia alimentare",
      "Convivialità e senso di appartenenza",
      "Regolazione dell'attenzione al pasto",
    ],
    sicurezza:
      "Cinture di sicurezza integrate fino ai 2-3 anni, poggiapiedi sempre a contatto con le pianta dei piedi, verificare la stabilità dei fissaggi ogni utilizzo.",
    consigli:
      "Il pasto è un rito di famiglia. Un seggiolone che mette il bambino alla stessa altezza dei genitori cambia tutto: lo sguardo si incontra, la relazione si nutre. Non è solo un accessorio, è una scelta educativa.",
    durata_noleggio: "6-24 mesi",
  },
  {
    id: "kit-bagno",
    nome: "Kit bagno bambino",
    categoria: "Cura & Routine",
    fascia: "0-24 mesi",
    fasciaKey: "0-2",
    breve: "Non solo igiene: il bagnetto è un rituale di cura profonda.",
    eta_consigliata: "Dalla nascita ai 24 mesi",
    periodo_utilizzo: "Fino a quando il bambino sta seduto stabile nella vasca grande",
    a_cosa_serve:
      "Un kit completo per il bagnetto del neonato e del bambino piccolo: vaschetta ergonomica, riduttore, accessori per rendere il momento sicuro, sereno e piacevole.",
    competenze: [
      "Consapevolezza corporea",
      "Rilassamento e regolazione",
      "Rituale del benessere",
      "Contatto sereno con l'adulto",
    ],
    sicurezza:
      "Acqua a temperatura 36-37°C, mai lasciare il bambino incustodito nemmeno un istante, verificare la stabilità della vaschetta prima di ogni uso.",
    consigli:
      "Il bagnetto non è solo igiene: è un momento di cura profonda. Rallenta, canta, guarda i suoi occhi. È lì che nasce la fiducia — molto più che nel bagnoschiuma.",
    durata_noleggio: "6-12 mesi",
  },
];

// ------------------- FILTRI -------------------
const FILTERS = [
  { key: "tutti", label: "Tutti" },
  { key: "0-2", label: "0-2 anni" },
  { key: "1-4", label: "1-4 anni" },
  { key: "6m-3+", label: "Evolutivo" },
];

// ------------------- HOW IT WORKS -------------------
const STEPS = [
  {
    n: "01",
    title: "Scegli",
    desc: "Sfoglia il Baule e leggi la Carta d'Identità di ogni oggetto: età, uso, competenze favorite, consigli.",
  },
  {
    n: "02",
    title: "Richiedi",
    desc: "Scrivimi su WhatsApp per verificare disponibilità, tempi e modalità di consegna.",
  },
  {
    n: "03",
    title: "Ricevi",
    desc: "Ritiro in zona Bologna e provincia, o consegna a domicilio concordata insieme.",
  },
  {
    n: "04",
    title: "Restituisci",
    desc: "Al termine del periodo concordato, ripasso a ritirare. Sanifico e preparo per la famiglia successiva.",
  },
];

const WHY = [
  {
    title: "Risparmio consapevole",
    body: "Molti oggetti servono solo per pochi mesi: comprarli nuovi è costoso e spesso inutile.",
  },
  {
    title: "Meno impatto ambientale",
    body: "Un oggetto che attraversa più famiglie è un piccolo gesto di cura verso il pianeta.",
  },
  {
    title: "Cura e sanificazione",
    body: "Ogni oggetto viene controllato e igienizzato prima di essere consegnato alla famiglia successiva.",
  },
  {
    title: "Consulenza inclusa",
    body: "Ogni noleggio è accompagnato dal mio sguardo educativo — come usarlo, quando, perché.",
  },
];

// ------------------- FAQ -------------------
const FAQS = [
  {
    q: "Quanto costa il noleggio?",
    a: "Ogni oggetto ha un costo dedicato che varia in base al periodo di utilizzo. Scrivimi su WhatsApp per un preventivo personalizzato: mi piace ascoltare prima la vostra situazione familiare.",
  },
  {
    q: "In che zona è disponibile il noleggio?",
    a: "Sono attiva a Bologna e provincia. Per zone limitrofe possiamo concordare la consegna insieme.",
  },
  {
    q: "Come vengono sanificati gli oggetti?",
    a: "Ogni oggetto viene controllato, pulito e igienizzato con prodotti sicuri per l'infanzia prima di essere consegnato a una nuova famiglia.",
  },
  {
    q: "Serve una cauzione?",
    a: "Sì, è prevista una cauzione simbolica a garanzia dell'oggetto. Ti restituisco tutto al momento della riconsegna, salvo danni.",
  },
  {
    q: "Posso prolungare il noleggio?",
    a: "Certo! Se scopriamo che l'oggetto vi accompagna ancora, si può sempre rinnovare il periodo. Basta scrivermi in tempo utile.",
  },
];

// ------------------- WHATSAPP HELPER -------------------
const waLink = (item) => {
  const msg = encodeURIComponent(
    `Ciao Teresa, vorrei info sul noleggio di "${item.nome}" dal Baule della Prima Infanzia 🧸`
  );
  return `${BRAND.whatsappUrl}?text=${msg}`;
};

// ============================================================
// PAGE
// ============================================================
export default function Baule() {
  const [filter, setFilter] = useState("tutti");
  const [openItem, setOpenItem] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  const filtered = ITEMS.filter((i) => (filter === "tutti" ? true : i.fasciaKey === filter));

  return (
    <div className="pt-24 md:pt-28" data-testid="baule-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[70vh] px-6 md:px-10 py-16 md:py-28 overflow-hidden bg-[#FAF8F5]"
      >
        <div className="max-w-[1500px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">
            — Il Baule
          </p>
          <h1
            className="font-display text-6xl md:text-8xl lg:text-[9rem] leading-[0.85] text-[#3D2140] mb-8"
            data-testid="baule-heading"
          >
            <RevealText text="Il Baule" delay={0.1} />
            <RevealText text={["della", "Prima Infanzia"]} className="text-[#FF2768] block" delay={0.2} />
          </h1>
          <p className="max-w-2xl text-lg md:text-xl text-[#3D2140]/80 leading-relaxed font-editorial italic">
            La biblioteca degli oggetti che accompagnano la crescita.
          </p>
          <p className="max-w-2xl mt-6 text-base md:text-lg text-[#3D2140]/70 leading-relaxed">
            Non tutto va comprato nuovo. Alcuni oggetti attraversano la crescita di un bambino in
            pochi mesi — e possono attraversare più famiglie, con cura e consapevolezza. Ogni oggetto
            del Baule ha la sua <em className="text-[#FF2768] not-italic font-semibold">Carta d'Identità</em>:
            non stai noleggiando un accessorio, stai ricevendo uno strumento educativo.
          </p>

          {/* floating bear */}
          <motion.img
            src={BRAND.logo}
            alt=""
            aria-hidden
            style={{ y: bearY, rotate: bearRotate }}
            className="absolute right-4 md:right-10 top-8 md:top-16 h-32 md:h-56 w-32 md:w-56 rounded-full opacity-80 animate-floaty drop-shadow-2xl pointer-events-none"
          />
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1500px] mx-auto" data-testid="baule-how">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Come funziona</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Quattro passi,<br/>
              <span className="text-[#81C3C9]">senza fretta.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white rounded-3xl p-8 border border-[#3D2140]/5 hover:border-[#FF2768]/30 transition-colors"
            >
              <span className="font-display text-5xl text-[#FF2768] leading-none">{s.n}</span>
              <h3 className="font-display text-3xl text-[#3D2140] mt-4 mb-3">{s.title}</h3>
              <p className="text-sm text-[#3D2140]/70 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATALOGO */}
      <section
        id="catalogo"
        className="px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white"
        data-testid="baule-catalog"
      >
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16 items-end">
            <div className="md:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Il catalogo</p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
                Apri il baule,<br/>
                <span className="text-[#81C3C9]">scopri gli oggetti.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <div className="flex flex-wrap gap-2 md:justify-end">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`baule-filter-${f.key}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                      filter === f.key
                        ? "bg-[#FF2768] border-[#FF2768] text-white"
                        : "bg-transparent border-white/25 text-white/80 hover:border-white"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, i) => (
              <motion.button
                key={item.id}
                type="button"
                onClick={() => setOpenItem(item)}
                data-testid={`baule-item-${item.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                whileHover={{ y: -6 }}
                className="text-left bg-[#3D2140] rounded-3xl p-8 border border-white/8 hover:border-[#FF2768]/60 transition-all group"
              >
                <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-[#FF2768]/25 via-[#81C3C9]/20 to-[#3D2140]/30 mb-6 flex items-center justify-center relative overflow-hidden">
                  <img
                    src={BRAND.logo}
                    alt=""
                    aria-hidden
                    className="h-24 w-24 md:h-32 md:w-32 rounded-full opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all"
                  />
                  <span className="absolute top-3 left-3 text-[9px] tracking-[0.3em] uppercase bg-white/10 backdrop-blur px-3 py-1 rounded-full text-white/80">
                    {item.categoria}
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl mb-2 group-hover:text-[#FF2768] transition-colors">
                  {item.nome}
                </h3>
                <p className="text-xs uppercase tracking-widest text-[#81C3C9] mb-4">
                  {item.fascia}
                </p>
                <p className="text-sm text-white/70 leading-relaxed mb-6">{item.breve}</p>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF2768] group-hover:gap-3 transition-all">
                  <Search size={16} />
                  Apri la Carta d'Identità
                  <ArrowRight size={14} />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* PERCHÉ NOLEGGIARE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1500px] mx-auto" data-testid="baule-why">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="md:col-span-6">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Perché noleggiare</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Un gesto piccolo,<br/>
              <span className="text-[#FF2768]">un pensiero grande.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {WHY.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#81C3C9]/15 rounded-3xl p-8 border-l-4 border-[#FF2768]"
            >
              <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] mb-3">{w.title}</h3>
              <p className="text-[#3D2140]/75 leading-relaxed">{w.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#FAF8F5]" data-testid="baule-faq">
        <div className="max-w-[1100px] mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Domande frequenti</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Le cose che<br/>
              <span className="text-[#81C3C9]">mi chiedete di più.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group bg-white rounded-2xl p-6 border border-[#3D2140]/8 open:border-[#FF2768]/40 transition-colors"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4 font-display text-2xl text-[#3D2140] list-none">
                  <span>{f.q}</span>
                  <span className="text-[#FF2768] text-3xl leading-none group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-[#3D2140]/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1500px] mx-auto text-center" data-testid="baule-cta">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Iniziamo</p>
        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl text-[#3D2140] leading-[0.9] mb-8">
          Un oggetto ti serve?<br/>
          <span className="text-[#FF2768]">Scrivimi.</span>
        </h2>
        <p className="max-w-xl mx-auto text-lg text-[#3D2140]/70 leading-relaxed mb-10">
          Sono io ad ascoltarti dall'altra parte. Nessun bot, nessun form freddo. Solo una
          conversazione tra persone che si prendono cura dei bambini.
        </p>
        <a
          href={BRAND.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="baule-whatsapp-cta"
          className="btn-tv-primary text-base md:text-lg px-8 py-4 md:px-10 md:py-5"
        >
          <MessageCircle size={18} />
          Scrivimi su WhatsApp
        </a>
      </section>

      {/* ------------------- MODAL CARTA D'IDENTITÀ ------------------- */}
      <AnimatePresence>
        {openItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-[#3D2140]/70 backdrop-blur-sm overflow-y-auto"
            onClick={() => setOpenItem(null)}
            data-testid="baule-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-3xl bg-[#FAF8F5] rounded-[24px] md:rounded-[36px] shadow-2xl my-auto overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="baule-modal"
            >
              <button
                onClick={() => setOpenItem(null)}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-10 p-2.5 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors"
                aria-label="Chiudi"
                data-testid="baule-modal-close"
              >
                <X size={20} className="text-[#3D2140]" />
              </button>

              {/* Header carta */}
              <div className="p-8 md:p-12 pb-6 bg-gradient-to-br from-[#FF2768] to-[#E01F5A] text-white">
                <p className="text-[10px] uppercase tracking-[0.4em] text-white/80 mb-3">
                  — Carta d'Identità
                </p>
                <h3 className="font-display text-4xl md:text-6xl leading-[0.9] mb-3">
                  {openItem.nome}
                </h3>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="text-[10px] uppercase tracking-[0.3em] bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                    {openItem.categoria}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.3em] bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                    {openItem.fascia}
                  </span>
                </div>
              </div>

              {/* Corpo carta */}
              <div className="p-8 md:p-12 space-y-8">
                {/* Grid due colonne età + periodo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <IdField icon={Calendar} label="Età consigliata" value={openItem.eta_consigliata} />
                  <IdField icon={Clock} label="Periodo di utilizzo" value={openItem.periodo_utilizzo} />
                </div>

                {/* A cosa serve */}
                <IdBlock icon={Target} label="A cosa serve">
                  <p className="text-[#3D2140]/80 leading-relaxed">{openItem.a_cosa_serve}</p>
                </IdBlock>

                {/* Competenze */}
                <IdBlock icon={Sparkles} label="Competenze favorite">
                  <ul className="space-y-2">
                    {openItem.competenze.map((c, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#3D2140]/80">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FF2768] flex-shrink-0" />
                        <span className="leading-relaxed">{c}</span>
                      </li>
                    ))}
                  </ul>
                </IdBlock>

                {/* Sicurezza */}
                <IdBlock icon={ShieldCheck} label="Uso in sicurezza">
                  <p className="text-[#3D2140]/80 leading-relaxed">{openItem.sicurezza}</p>
                </IdBlock>

                {/* Consigli di Teresa */}
                <div className="bg-[#81C3C9]/20 rounded-2xl p-6 md:p-7 border-l-4 border-[#FF2768]">
                  <div className="flex items-center gap-3 mb-3">
                    <Heart size={18} className="text-[#FF2768]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-semibold">
                      Il consiglio di Teresa
                    </span>
                  </div>
                  <p className="text-[#3D2140] leading-relaxed italic font-editorial text-base md:text-lg">
                    "{openItem.consigli}"
                  </p>
                </div>

                {/* Durata media */}
                <IdField icon={Calendar} label="Durata media noleggio" value={openItem.durata_noleggio} />

                {/* CTA */}
                <a
                  href={waLink(openItem)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="baule-modal-whatsapp"
                  className="btn-tv-primary w-full justify-center text-base py-4"
                >
                  <MessageCircle size={18} />
                  Richiedi disponibilità su WhatsApp
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- helper components ----------
function IdField({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
      <div className="flex items-center gap-2.5 mb-2">
        <Icon size={16} className="text-[#FF2768]" />
        <span className="text-[9px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold">
          {label}
        </span>
      </div>
      <p className="text-[#3D2140] font-semibold">{value}</p>
    </div>
  );
}

function IdBlock({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-3">
        <Icon size={16} className="text-[#FF2768]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}
